import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Menu, Avatar, Dropdown, Badge, Modal, Form, Input, message } from 'antd';
import {
  DashboardOutlined, TeamOutlined, BarChartOutlined,
  TrophyOutlined, BellOutlined, ImportOutlined, FlagOutlined,
  UserOutlined, LogoutOutlined, BulbOutlined, SafetyCertificateOutlined,
  HistoryOutlined, TagsOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { auth } from '../api';

const { Header, Sider, Content } = Layout;

const ROLE_LABEL = {
  gm: '总经理',
  dept_manager: '部门主管',
  staff: '员工'
};

const T = {
  gold: '#D4AF37',
  goldBright: '#F4C84A',
  navy: '#0A1A3F',
  sider: 200,
  header: 60,
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdForm] = Form.useForm();
  const [alertUnread, setAlertUnread] = useState(0);
  const [beijingTime, setBeijingTime] = useState('');

  // 北京时间时钟
  useEffect(() => {
    const fmt = () => {
      const now = new Date();
      const bj = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }));
      const Y = bj.getFullYear();
      const M = String(bj.getMonth() + 1).padStart(2, '0');
      const D = String(bj.getDate()).padStart(2, '0');
      const h = String(bj.getHours()).padStart(2, '0');
      const m = String(bj.getMinutes()).padStart(2, '0');
      const s = String(bj.getSeconds()).padStart(2, '0');
      setBeijingTime(`${Y}-${M}-${D} ${h}:${m}:${s}`);
    };
    fmt();
    const timer = setInterval(fmt, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchAlertCount = useCallback(async () => {
    try {
      const res = await api.get('/alerts?is_read=0&page_size=1');
      setAlertUnread(res.data?.unread_count || 0);
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchAlertCount();
    const timer = setInterval(fetchAlertCount, 60000);
    return () => clearInterval(timer);
  }, [fetchAlertCount]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) { setCollapsed(true); setMobileMenuOpen(false); }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [];
  menuItems.push({ key: '/dashboard', icon: <DashboardOutlined />, label: '业绩看板' });
  menuItems.push({ key: '/management', icon: <TeamOutlined />, label: '组织架构' });
  menuItems.push({ key: '/biz-types', icon: <TagsOutlined />, label: user.role === 'staff' ? '积分规则' : '业务类型' });
  if (user.role !== 'staff') {
    menuItems.push({ key: '/targets', icon: <FlagOutlined />, label: '目标管理' });
  }
  menuItems.push({ key: '/performance', icon: <BarChartOutlined />, label: user.role === 'staff' ? '我的业绩' : '业务明细' });
  menuItems.push({ key: '/leaderboard', icon: <TrophyOutlined />, label: '排行榜' });
  menuItems.push({
    key: '/alerts', icon: <BellOutlined />,
    label: (
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>预警中心</span>
        {alertUnread > 0 && (
          <Badge count={alertUnread} size="small" style={{ transform: 'scale(0.8)', marginLeft: 4 }} />
        )}
      </span>
    ),
  });
  if (user.role !== 'staff') {
    menuItems.push({ key: '/import', icon: <ImportOutlined />, label: '数据导入' });
  }
  menuItems.push({ key: '/analysis', icon: <BulbOutlined />, label: 'AI分析' });
  if (user.role === 'gm' || user.role === 'dept_manager') {
    menuItems.push({ key: '/accounts', icon: <SafetyCertificateOutlined />, label: '账号管理' });
  }
  if (user.role === 'gm') {
    menuItems.push({ key: '/logs', icon: <HistoryOutlined />, label: '操作日志' });
  }

  const selectedKey = '/' + location.pathname.split('/')[1];

  const dropdownItems = {
    items: [
      { key: 'pwd', icon: <SafetyCertificateOutlined />, label: '修改密码' },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
    ],
    onClick: ({ key }) => {
      if (key === 'pwd') {
        pwdForm.resetFields();
        setPwdOpen(true);
      } else if (key === 'logout') {
        logout();
        navigate('/login');
      }
    }
  };

  const handleChangePassword = async (values) => {
    setPwdLoading(true);
    try {
      await auth.changePassword(values);
      message.success('密码修改成功');
      setPwdOpen(false);
    } catch (err) {
      message.error(err?.message || '修改失败');
    } finally {
      setPwdLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile && mobileMenuOpen && (
        <div className="mobile-mask" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* 侧边栏：藏蓝底 */}
      <Sider
        width={T.sider}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        className={isMobile ? (mobileMenuOpen ? 'mobile-sider-show' : 'mobile-sider-hidden') : ''}
        style={{
          position: 'fixed', left: 0, top: 0, bottom: 0,
          zIndex: isMobile ? 101 : 100,
          overflow: 'auto',
        }}
      >
        {/* Logo */}
        <div style={{
          height: T.header,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 16px',
          borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
          position: 'relative',
        }}>
          {/* 顶部金色装饰条 */}
          <div style={{
            position: 'absolute', top: 0, left: 16, right: 16, height: 1,
            background: `linear-gradient(90deg, transparent, ${T.gold}, transparent)`,
            boxShadow: `0 0 8px ${T.gold}`,
          }} />
          {!collapsed ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 30, height: 30, borderRadius: 6,
                background: `linear-gradient(135deg, ${T.gold} 0%, ${T.goldBright} 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: T.navy, fontSize: 15, fontWeight: 700,
                boxShadow: `0 2px 12px rgba(212, 175, 55, 0.4)`,
              }}>游</div>
              <span style={{
                color: '#fff', fontSize: 15, fontWeight: 600,
                letterSpacing: 0.5, whiteSpace: 'nowrap',
              }}>游暮酒店管理</span>
            </div>
          ) : (
            <div style={{
              width: 30, height: 30, borderRadius: 6,
              background: `linear-gradient(135deg, ${T.gold} 0%, ${T.goldBright} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.navy, fontSize: 15, fontWeight: 700,
              boxShadow: `0 2px 12px rgba(212, 175, 55, 0.4)`,
            }}>游</div>
          )}
        </div>

        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>

      {/* 右侧 */}
      <Layout
        className={isMobile ? 'mobile-layout' : ''}
        style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : T.sider), transition: 'margin-left .2s' }}
      >
        {/* 顶部导航：藏蓝→鎏金渐变 */}
        <Header
          className={isMobile ? 'mobile-header' : ''}
          style={{
            position: 'sticky', top: 0, zIndex: 99,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          {/* 左侧：菜单图标 + 时间 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              onClick={() => isMobile ? setMobileMenuOpen(true) : setCollapsed(!collapsed)}
              style={{
                color: '#fff', fontSize: 18, cursor: 'pointer',
                display: 'flex', alignItems: 'center',
                padding: 6, borderRadius: 6,
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(212, 175, 55, 0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </div>
            {beijingTime && !isMobile && (
              <span style={{
                fontSize: 13, color: 'rgba(255,255,255,0.95)',
                fontFamily: 'SF Mono, Menlo, monospace',
                letterSpacing: 0.5,
                textShadow: '0 0 8px rgba(212, 175, 55, 0.3)',
              }}>{beijingTime}</span>
            )}
          </div>

          {/* 右侧：通知 + 用户 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Badge count={alertUnread} size="small" offset={[-4, 4]}>
              <BellOutlined
                onClick={() => navigate('/alerts')}
                style={{
                  color: '#fff', fontSize: 18, cursor: 'pointer',
                  padding: 6, borderRadius: 6,
                }}
              />
            </Badge>

            <Dropdown menu={dropdownItems} placement="bottomRight">
              <div style={{
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                padding: '4px 10px 4px 4px', borderRadius: 8,
                transition: 'background 0.2s',
                border: '1px solid rgba(212, 175, 55, 0.2)',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(212, 175, 55, 0.10)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Avatar
                  size={32}
                  icon={<UserOutlined />}
                  style={{
                    background: `linear-gradient(135deg, ${T.gold} 0%, ${T.goldBright} 100%)`,
                    color: T.navy,
                    fontWeight: 600,
                    border: '1px solid rgba(212, 175, 55, 0.4)',
                  }}
                />
                {!isMobile && (
                  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                    <span style={{ color: '#fff', fontSize: 13, fontWeight: 500 }}>{user.name}</span>
                    <span style={{ color: T.gold, fontSize: 11, letterSpacing: 0.5 }}>
                      {ROLE_LABEL[user.role]}
                    </span>
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="page-content" style={{ margin: 0, padding: '24px 24px 48px', minHeight: 280 }}>
          <Outlet />
        </Content>

        {/* 全局页脚备案号 */}
        <div style={{
          position: 'fixed', bottom: 0,
          left: isMobile ? 0 : (collapsed ? 80 : T.sider),
          right: 0, zIndex: 50,
          textAlign: 'center', fontSize: 12, color: '#6B7280', letterSpacing: 0.5,
          padding: '8px 0',
          background: 'rgba(245,246,247,0.92)',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          backdropFilter: 'blur(4px)',
        }}>
          <a href="https://beian.miit.gov.cn" target="_blank" rel="noreferrer" style={{ color: '#6B7280', textDecoration: 'none' }}>浙ICP备 2026052534号</a>
          <span style={{ margin: '0 8px' }}>·</span>
          <span>© 2026 游暮酒店管理</span>
        </div>
      </Layout>

      <Modal
        title="修改密码"
        open={pwdOpen}
        onCancel={() => setPwdOpen(false)}
        onOk={() => pwdForm.submit()}
        confirmLoading={pwdLoading}
        okText="确认修改"
        cancelText="取消"
      >
        <Form form={pwdForm} onFinish={handleChangePassword} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="old_password" label="原密码" rules={[{ required: true, message: '请输入原密码' }]}>
            <Input.Password placeholder="输入原密码" />
          </Form.Item>
          <Form.Item name="new_password" label="新密码" rules={[
            { required: true, message: '请输入新密码' },
            { min: 6, message: '密码至少6位' },
          ]}>
            <Input.Password placeholder="输入新密码（至少6位）" />
          </Form.Item>
          <Form.Item name="confirm" label="确认新密码" dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
